import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode, GetDefinitionNode } from './testUtil';

type Result = ReturnType<typeof useSavedQueryByEnvironment>[0];

export type SavedQueryResponse = NonNullable<Result['data']>;

export type RequestedSavedQueryAppliedNode = GetAppliedNode<
  'savedQueries',
  SavedQueryResponse
>;

export type RequestedSavedQueryDefinitionNode = GetDefinitionNode<
  'savedQueries',
  SavedQueryResponse
>;

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetSavedQueryByUniqueId = graphql(/* GraphQL */ `
  query SavedQuery($environmentId: BigInt!, $uniqueId: String!, $userId: BigInt) {
    environment(id: $environmentId, userId: $userId) {
      definition {
        lastUpdatedAt
        savedQueries(first: 1, filter: { uniqueIds: [$uniqueId] }) {
          edges {
            node {
              fqn
              uniqueId
              name
              tags
              description
              packageName
              filePath
              projectId
              runGeneratedAt
              group
              parents {
                name
                uniqueId
                resourceType
                __typename
              }
              children {
                uniqueId
              }
              queryParams {
                groupBy
                limit
                metrics
                orderBy
                where {
                  whereFilters {
                    whereSqlTemplate
                  }
                }
              }
              exports {
                name
                config {
                  alias
                  exportAs
                  schema
                  database
                }
              }
            }
          }
        }
      }
    }
  }
`);

type SavedQueryByEnvironmentParams = {
  environmentId: number | undefined;
  uniqueId: string;
  userId: number | 'loading' | null;
  pause?: boolean;
};

export const useSavedQueryByEnvironment = ({
  environmentId = 0,
  uniqueId,
  userId,
  pause,
}: SavedQueryByEnvironmentParams) => {
  const query = useQueryStable({
    query: GetSavedQueryByUniqueId,
    variables: { environmentId, uniqueId, userId: userId as number | null },
    pause: pause || !environmentId || !uniqueId || userId === 'loading',
  });

  useCacheMonitor({
    definition: query[0].data?.environment.definition?.lastUpdatedAt,
  });

  return query;
};
